import { Injectable } from '@angular/core';
import { AbstractI18nService, GoogleApiPlace } from '@libs/index';
import PlaceSearchRequest = google.maps.places.PlaceSearchRequest;
import PlaceDetailsRequest = google.maps.places.PlaceDetailsRequest;
import { GOOGLE_FIND_PLACE_MAX_RADIUS } from '../../models/consts/Other.const';
import { ServerCallerService } from '@libs/index';
import { ErrorHandlerServiceService } from '@libs/index';
import { UrlUtils } from '@libs/index';
import { ScriptLoaderService } from '@libs/index';
import { EnvUtils } from '../../commons/utils/env.utils';
import AutocompletePrediction = google.maps.places.AutocompletePrediction;
import { GOOGLE_LANGUAGE_CODE } from '@libs/index';
import unidecode from 'unidecode';

// cannot use web-service because of CORS policy!!!

@Injectable({
  providedIn: 'root',
})
export class GoogleApiService {
  private readonly PHOTO_MAX_WIDTH = 800;
  private readonly GOOGLE_PLACE_API_URL =
    'https://maps.googleapis.com/maps/api/place';

  private searchPaginationNextPageToken: string;

  constructor(
    private i18nService: AbstractI18nService,
    private scriptLoaderService: ScriptLoaderService,
    private serverCallerService: ServerCallerService,
    private errorHandlerServiceService: ErrorHandlerServiceService
  ) {}

  public async init() {
    // init for @angular/google-maps
    await this.scriptLoaderService.loadScript(
      UrlUtils.addParameterToUrl(`https://maps.googleapis.com/maps/api/js`, {
        libraries: 'places',
        key: EnvUtils.getGoogleApiKey(),
        // To ignore warning from google api
        // see: https://stackoverflow.com/questions/75179573/how-to-fix-loading-the-google-maps-javascript-api-without-a-callback-is-not-supp
        callback: 'Function.prototype',
      })
    );
  }

  // see: https://developers.google.com/maps/documentation/places/web-service/autocomplete
  public async fetchPlaceAutocomplete(
    text: string
  ): Promise<AutocompletePrediction[]> {
    const requestParameters = {
      input: unidecode(text),
      language: this.getLanguageCode(),
      type: '(regions)',
    };

    const googleUrl = UrlUtils.addParameterToUrl(
      `${this.GOOGLE_PLACE_API_URL}/autocomplete/json`,
      requestParameters
    );

    try {
      const responseBody = await this.callCachedInBackendServer(googleUrl);
      return responseBody?.predictions;
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
      return [];
    }
  }

  public async findPlaceByPlaceId(placeId: string): Promise<GoogleApiPlace> {
    if (!placeId?.length) {
      return null;
    }

    const request: PlaceDetailsRequest = {
      placeId,
      fields: [
        'formatted_address',
        'name',
        'geometry',
        'place_id',
        'rating',
        'user_ratings_total',
        'photos',
        'opening_hours',
        'formatted_phone_number',
      ],
    };

    // see: https://developers.google.com/maps/documentation/places/web-service/details
    const googleUrl = `${
      this.GOOGLE_PLACE_API_URL
    }/details/json?fields=${request.fields.join(
      ','
    )}&place_id=${placeId}&language=${this.getLanguageCode()}`;

    try {
      const responseBody = await this.callCachedInBackendServer(googleUrl);
      return this.formatResultFromBackend(responseBody?.result);
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
      return null;
    }
  }

  // see: https://developers.google.com/maps/documentation/javascript/places#place_search_requests
  public async findSalonNearby(
    lat: number,
    lng: number,
    radiusInKm: number,
    keyword?: string
  ): Promise<GoogleApiPlace[]> {
    const request: PlaceSearchRequest = {
      location: { lat, lng },
      radius: Math.min(radiusInKm, GOOGLE_FIND_PLACE_MAX_RADIUS) * 1000, // max radius for google api = 50km
      keyword: keyword?.length ? `${keyword} salon` : 'nails salon',
      type: 'beauty_salon',
    };

    const googleUrl = `${this.GOOGLE_PLACE_API_URL}/nearbysearch/json?keyword=${request.keyword}&location=${request.location.lat},${request.location.lng}&radius=${request.radius}&type=${request.type}`;

    const responseBody = await this.callCachedInBackendServer(googleUrl);
    this.searchPaginationNextPageToken = responseBody?.next_page_token;

    const results = await this.loadGooglePlacePhotoForSalonList(
      responseBody?.results
    );
    return results?.map((r) => this.formatResultFromBackend(r));
  }

  public async findCurrentPosition(
    lat: number,
    lng: number
  ): Promise<GoogleApiPlace> {
    const request: PlaceSearchRequest = {
      location: { lat, lng },
      radius: 1,
    };

    const googleUrl = `${this.GOOGLE_PLACE_API_URL}/nearbysearch/json?location=${request.location.lat},${request.location.lng}&radius=${request.radius}`;

    const responseBody = await this.callCachedInBackendServer(googleUrl);
    const results = responseBody?.results?.map((r) =>
      this.formatResultFromBackend(r)
    );

    return results?.[0] || null;
  }

  public isMoreGoogleSalonToLoad(): boolean {
    return !!this.searchPaginationNextPageToken?.length;
  }

  public async getNextSalonInPagination(): Promise<GoogleApiPlace[]> {
    if (!this.isMoreGoogleSalonToLoad()) {
      return null;
    }

    const googleUrl = `${this.GOOGLE_PLACE_API_URL}/nearbysearch/json?pagetoken=${this.searchPaginationNextPageToken}`;

    const responseBody = await this.callCachedInBackendServer(googleUrl);

    this.searchPaginationNextPageToken = responseBody?.next_page_token;
    const results = await this.loadGooglePlacePhotoForSalonList(
      responseBody?.results
    );

    return results?.map((r) => this.formatResultFromBackend(r));
  }

  private async loadGooglePlacePhotoForSalonList(
    googlePlaces: GoogleApiPlace[],
    maxNrPhoto = 1
  ): Promise<GoogleApiPlace[]> {
    const requestPromises = googlePlaces?.map((p) =>
      this.loadGooglePlacePhotoForSalon(p, maxNrPhoto)
    );
    return await Promise.all(requestPromises);
  }

  public async loadGooglePlacePhotoForSalon(
    googlePlace: GoogleApiPlace,
    maxNrPhoto = 1
  ): Promise<GoogleApiPlace> {
    if (maxNrPhoto <= 0 || !googlePlace?.photos?.length) {
      return googlePlace;
    }

    try {
      const requestPromises = [];

      googlePlace.photos.forEach((p, index) => {
        if (index < maxNrPhoto) {
          // @ts-ignore
          requestPromises.push(this.getGooglePlacePhoto(p?.photo_reference));
        }
      });

      const responseResults = await Promise.all(requestPromises);
      googlePlace.photoUrls = responseResults
        ?.map((r) => r?.url)
        .filter((url) => !!url);
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    }

    return googlePlace;
  }

  private getGooglePlacePhoto(photoReference: string) {
    const googleUrl = `${this.GOOGLE_PLACE_API_URL}/photo?maxwidth=${this.PHOTO_MAX_WIDTH}&photo_reference=${photoReference}`;
    return this.callCachedInBackendServer(googleUrl);
  }

  private formatResultFromBackend(result: any): GoogleApiPlace {
    if (!result) {
      return result;
    }

    return {
      ...result,
      geometryLocationData: {
        lat: result.geometry?.location?.lat,
        lng: result.geometry?.location?.lng,
      },
    };
  }

  private async callCachedInBackendServer(googleUrl: string) {
    const url = '/v1/google-requests';

    const response = await this.serverCallerService.post(url, {
      url: googleUrl,
    });

    return response?.body;
  }

  private getLanguageCode(): GOOGLE_LANGUAGE_CODE {
    const language = this.i18nService.getSelectedLanguage();
    return language.googleLanguageCode || GOOGLE_LANGUAGE_CODE.EN;
  }
}
